// =============================================================================
// RESUME TEMPLATE REGISTRY (client) — TEMPLATES-P2, docs/design/
// resume-template-library-2026-07-05.md §3/§7/§8/§9/§10.
//
// THE SEAM: "one template definition drives all three export paths." The
// SHARED pure logic (meta constants, accent palette + resolveAccent/
// effectiveAccent, section ordering, page geometry, per-family tokens) lives
// in @careeropt/schema's resume_templates_registry.ts — the SAME module the
// server (apps/api's DriveExportService) imports, so screen/docx/pdf/Drive-
// HTML can never independently drift on "what does family X mean." THIS file
// adds ONLY the one thing that's genuinely CLIENT-specific: which families
// have real RENDERER CODE in this app today (REGISTERED_FAMILIES) and the
// client-scoped isBuiltFamily/resolvePreferredFamily that check against it.
//
// FOUR FAMILIES ARE 'built' TODAY: ats-plain, counter-ready, recent-graduate,
// modern-professional. The other 10 are 'coming' registry stubs — this
// module never renders a 'coming' family (resumeTemplatePdf.tsx's
// ResumeTemplatePdfDocument throws a descriptive error if a caller tries;
// every UI call site checks isBuiltFamily first, so this is a "should never
// happen, defensive" throw, not a reachable user-facing path).
// =============================================================================

import {
  DEFAULT_ACCENT_KEY,
  DEFAULT_TEMPLATE_FAMILY,
  RESUME_TEMPLATE_META,
  RESUME_TEMPLATE_META_BY_FAMILY,
  RESUME_ACCENT_OPTIONS,
  applyTemplateDisplayOrder,
  effectiveAccent,
  orderSectionsForFamily,
  pageGeometryForVariant,
  resolveAccent,
  tokensForFamily,
  type PageGeometryMm,
  type ResumeAccentKey,
  type ResumeAccentOption,
  type ResumeTemplateFamily,
  type ResumeTemplateMeta,
  type ResumeTemplateTokens,
} from '@careeropt/schema';

// Re-export the shared logic verbatim so every existing import site in this
// app (`from './resumeTemplateRegistry'`) keeps working unchanged — this
// file is the client's OWN single import point, even though the bodies now
// live in the shared package.
export {
  DEFAULT_ACCENT_KEY,
  DEFAULT_TEMPLATE_FAMILY,
  RESUME_ACCENT_OPTIONS,
  RESUME_TEMPLATE_META,
  RESUME_TEMPLATE_META_BY_FAMILY,
  applyTemplateDisplayOrder,
  effectiveAccent,
  orderSectionsForFamily,
  pageGeometryForVariant,
  resolveAccent,
  tokensForFamily,
};
export type {
  PageGeometryMm,
  ResumeAccentKey,
  ResumeAccentOption,
  ResumeTemplateFamily,
  ResumeTemplateMeta,
  ResumeTemplateTokens,
};

/** The 4 families with real renderer code in THIS app today. Kept as an
 *  explicit, independently-declared set (not merely re-derived from
 *  RESUME_TEMPLATE_META's status field at import time) so a registry test
 *  can assert the DATA ('built' status in the shared meta) and the CODE
 *  (this set, plus every renderer function's own switch) agree — the "row
 *  <-> code mutual check" the design calls for, made concrete. */
export const REGISTERED_FAMILIES: readonly ResumeTemplateFamily[] = [
  'ats-plain',
  'counter-ready',
  'recent-graduate',
  'modern-professional',
];

/** Every family the shared meta marks 'coming' — the registry stubs.
 *  Derived (not hand-listed) so adding a 15th family to the meta
 *  automatically shows up here without a second edit. */
export function stubFamilies(): readonly ResumeTemplateFamily[] {
  return RESUME_TEMPLATE_META.filter((m) => m.status === 'coming').map((m) => m.family);
}

/** All picker-ordered meta rows, sorted by sortOrder (ties by family string
 *  for determinism) — the exact list + order the picker renders. */
export function orderedTemplateMeta(): readonly ResumeTemplateMeta[] {
  return [...RESUME_TEMPLATE_META].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.family.localeCompare(b.family),
  );
}

/** True iff `family` has real renderer code in THIS app (REGISTERED_FAMILIES)
 *  AND the shared meta agrees it's 'built' — the two must never disagree; if
 *  they ever do, this reads FALSE (fail closed). */
export function isBuiltFamily(family: string): family is ResumeTemplateFamily {
  const meta = RESUME_TEMPLATE_META_BY_FAMILY[family as ResumeTemplateFamily];
  return (
    meta != null && meta.status === 'built' && REGISTERED_FAMILIES.includes(family as ResumeTemplateFamily)
  );
}

/** True iff `family` is a real family name in the shared meta (built OR
 *  coming) — narrower than isBuiltFamily, used for "is this even a family we
 *  know about" input validation. */
export function isKnownFamily(family: string): family is ResumeTemplateFamily {
  return RESUME_TEMPLATE_META_BY_FAMILY[family as ResumeTemplateFamily] != null;
}

/** True iff `family` requires the template_library capability to EXPORT. An
 *  unrecognised family fails closed (reads as premium) — mirrors the shared
 *  schema's own isPremiumFamily posture exactly. */
export function isPremiumFamily(family: string): boolean {
  const meta = RESUME_TEMPLATE_META_BY_FAMILY[family as ResumeTemplateFamily];
  return meta ? meta.tier === 'premium' : true;
}

/** Resolve the family a user should see selected: their stored preference
 *  (users.preferredTemplateFamily) when it names a real, BUILT family in
 *  THIS app; the documented default otherwise. Never a fabricated "last
 *  used" family, never silently downgrades a real premium choice for a free
 *  user here (that gating happens at EXPORT time server-side, per §6 — the
 *  picker/preview shows whichever family is actually selected, premium-
 *  locked or not, and the honest block only appears on an export attempt). */
export function resolvePreferredFamily(preferred: string | null | undefined): ResumeTemplateFamily {
  if (preferred && isBuiltFamily(preferred)) return preferred;
  return DEFAULT_TEMPLATE_FAMILY;
}
